/**
 * Khối dựng chung cho bản kê chi tiết lương.
 *
 * Bảng kê là màn hình đọc trên điện thoại giữa ca, không phải báo cáo in A4.
 * Nên khuôn ở đây là: điện thoại thấy ĐỦ để đối chiếu (ngày · khách · tiền),
 * màn hình rộng mới bung thêm cột mã đơn và sản phẩm. Không dùng cuộn ngang —
 * bảng cuộn ngang trên điện thoại là bảng không ai đọc hết.
 */

import type { ReactNode } from "react";
import { keepPayUnitTogether } from "@/features/salary-rules-v2/pay-unit";
import { vnd } from "../../constants";
import { priceTierText, type PriceTier } from "../../salaryDetail";

/**
 * Một mục của bản kê: tiêu đề, số đếm, tổng tiền của mục, rồi nội dung.
 *
 * `total` là tiền của CHÍNH mục này, luôn hiện cạnh tiêu đề — người đọc gấp
 * chỉ nhìn dòng tiêu đề là biết mục nào đáng mở ra soi.
 */
export function DetailSection({
  title,
  countLabel,
  total,
  note,
  children,
  testId,
}: {
  /** Biểu tượng cũ của khối. Áo tạp chí (15/10/2026) không vẽ nữa — giữ trường để các khối
   *  gọi không phải đổi; số chuyên mục tự đếm thay cho biểu tượng. */
  icon: string;
  title: string;
  countLabel?: string | undefined;
  total?: number | undefined;
  note?: ReactNode;
  children: ReactNode;
  testId?: string | undefined;
}) {
  return (
    // Áo chuyên mục của trang Lương kiểu tạp chí: ô số để trống, bộ đếm CSS của danh sách
    // (`.lt-ds-chuong` ở SalaryDetailView) tự đánh 01, 02… theo các khối thật sự hiện.
    <section data-testid={testId} className="lt-chuong">
      <span className="lt-so-chuong" aria-hidden="true" />
      <div className="lt-chuong-than">
        <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h3 className="text-[17px] font-semibold">
            {title}
            {countLabel ? (
              <span className="ml-1.5 font-normal text-muted-foreground">
                · {countLabel}
              </span>
            ) : null}
          </h3>
          {total != null ? (
            <span className="lt-so text-[24px] leading-none">{vnd(total)}</span>
          ) : null}
        </div>
        {note ? <p className="lt-loi-moi mb-2 text-[16px]">{note}</p> : null}
        {children}
      </div>
    </section>
  );
}

/**
 * Đầu một nhóm việc trong mục: tên nhóm, tiền của cả nhóm, và dưới tên là các
 * mức "12 khách × 3 thạch lương thảo (= 30.000đ)" (CEO chốt 15b 16/09/2026).
 *
 * Không tách được đơn giá (`tiers` null) thì giữ kiểu cũ "tên × số lượng":
 * thà không ghi giá còn hơn ghi một giá làm tròn. Bỏ `pay` là không hiện tiền
 * nhóm — phòng không trả theo từng đơn.
 */
export function GroupHeading({
  label,
  count,
  pay,
  tiers,
  noun,
}: {
  label: string;
  count: number;
  pay?: number | undefined;
  tiers: PriceTier[] | null;
  noun?: string | undefined;
}) {
  return (
    <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
      <span className="font-medium">
        {label}
        {tiers ? (
          tiers.map((t) => (
            <span key={t.unitPay} className="block text-xs font-normal text-muted-foreground">
              {keepPayUnitTogether(priceTierText(t, noun))}
            </span>
          ))
        ) : (
          <>
            {" "}
            <span className="font-normal text-muted-foreground">× {count}</span>
          </>
        )}
      </span>
      {pay != null ? (
        <span className="tabular-nums text-muted-foreground">{vnd(pay)}</span>
      ) : null}
    </div>
  );
}

/**
 * Bảng hai cột "tên khoản — số tiền". Dùng cho các bảng đã được máy chủ tính
 * sẵn theo loại việc (hậu kỳ, CSKH, lễ tân) và cho các khoản thưởng.
 */
export function AmountTable({ children }: { children: ReactNode }) {
  return (
    <table className="lt-ke-bang text-sm">
      <tbody>{children}</tbody>
    </table>
  );
}

export function AmountRow({
  label,
  hint,
  value,
  strong,
  testId,
}: {
  label: string;
  hint?: string | undefined;
  value: number | string;
  strong?: boolean | undefined;
  testId?: string | undefined;
}) {
  return (
    <tr data-testid={testId} className={strong ? "font-semibold" : undefined}>
      <td className="px-1 py-1.5 md:px-2">
        {label}
        {hint ? (
          <span className="block text-xs font-normal text-muted-foreground">
            {hint}
          </span>
        ) : null}
      </td>
      <td className="px-1 py-1.5 text-right tabular-nums md:px-2">
        {typeof value === "number" ? vnd(value) : value}
      </td>
    </tr>
  );
}

/** Hai kiểu xếp một mục bản kê: liền một danh sách theo ngày, hay chia nhóm. */
export type LineOrder = "date" | "group";

/**
 * Hai nút "Theo ngày · Theo gói" ở đầu mục (CEO chốt 1a · 2b, 30/10/2026).
 *
 * Mặc định "Theo ngày" vì đó là cách nhân sự dò lại tháng của mình; "Theo
 * gói"/"Theo loại" giữ cho người soát giá từng nhóm. Không nhớ lựa chọn giữa
 * các lần mở: bảng mở ra luôn giống nhau thì ai hướng dẫn ai cũng nói được.
 * Mượn đúng áo nút "Khu vực" của trang (`.lt-khu`) để hai cụm nút đọc như nhau.
 */
export function LineOrderSwitch({
  value,
  onChange,
  groupLabel,
}: {
  value: LineOrder;
  onChange: (next: LineOrder) => void;
  groupLabel: string;
}) {
  const options: { id: LineOrder; label: string }[] = [
    { id: "date", label: "Theo ngày" },
    { id: "group", label: groupLabel },
  ];
  return (
    <div className="lt-khu mb-2" role="group" aria-label="Cách xếp">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={value === o.id}
          onClick={() => onChange(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Bảng liệt kê từng dòng việc.
 *
 * Cột "Mã đơn" và "Sản phẩm" ẩn dưới 768px (`hidden md:table-cell`) — chúng
 * là thông tin tra cứu, không phải thông tin đối chiếu. Ngày + tên khách đủ
 * để một người nhớ ra buổi đó; mã đơn chỉ cần khi đi hỏi lại kế toán, lúc đó
 * họ đã ngồi trước máy tính.
 */
export function LineTable({
  columns,
  children,
}: {
  columns: { key: string; label: string; className?: string | undefined }[];
  children: ReactNode;
}) {
  return (
    <table className="lt-bang text-sm">
      <thead>
        <tr className="text-left">
          {columns.map((c) => (
            <th key={c.key} className={`px-1 py-1 font-normal md:px-2 ${c.className ?? ""}`}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

/**
 * Các dòng chú thích dưới số tiền của một dòng bản kê — bảng đơn và bảng phát
 * sinh dùng chung, để hai bảng đọc phép tính cùng một kiểu.
 *
 * `notes` là phép tính của dòng, chỉ bằng thạch lương thảo (phần của mình ·
 * bậc và hệ số · kết quả · phần rào bù) — màu mờ, đọc khi cần. Bề ngang có
 * trần: để tự do là dòng dài nhất giành hết bề ngang của cột tên khách trên
 * điện thoại. Dòng dài hơn trần thì xuống dòng, không đẩy bảng tràn ngang.
 * `blocked` là lý do dòng CHƯA ra tiền, hoặc đang được TRẢ TẠM (hai thứ không
 * bao giờ đi cùng nhau trên một dòng) — CỐ Ý không mờ: cả điểm của nó là để
 * người đọc NHÌN THẤY, chứ 0đ im lặng thì vốn đã có sẵn. Không tự đặt lại câu
 * ở đây: máy lương biết chính xác đơn thiếu gì, giao diện đoán lại là hai bên
 * lệch nhau mà không gì kêu lên.
 */
export function PayNotes({ notes, blocked }: { notes: string[]; blocked?: string | undefined }) {
  return (
    <>
      {notes.map((note) => (
        <div
          key={note}
          className="ml-auto max-w-[10rem] whitespace-normal text-[10px] font-normal text-muted-foreground"
        >
          {keepPayUnitTogether(note)}
        </div>
      ))}
      {blocked ? (
        <div className="whitespace-normal text-left text-[10px] font-normal text-amber-800">
          {keepPayUnitTogether(blocked)}
        </div>
      ) : null}
    </>
  );
}
